import React, { useEffect, useState } from "react";
import { Receipt, Loader2, ScrollText } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import AccountSelect from "@/components/AccountSelect";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function PaymentDialog({ customer, onClose, onSaved }) {
  const [mode, setMode] = useState("cash"); // cash | cheque
  const [form, setForm] = useState({ amount: "", method: "Nakit", description: "", date: todayISO(), account_id: "" });
  const [cheque, setCheque] = useState({ bank: "", cheque_no: "", due_date: todayISO() });
  const [saving, setSaving] = useState(false);
  const [sugs, setSugs] = useState([]);
  const [showSugs, setShowSugs] = useState(false);
  useEffect(() => { if (customer) { setMode("cash"); setForm((f) => ({ amount: "", method: f.method, description: "", date: todayISO(), account_id: f.account_id })); setCheque({ bank: "", cheque_no: "", due_date: todayISO() }); } }, [customer]);

  useEffect(() => {
    if (!customer || !showSugs) return;
    const t = setTimeout(() => api.get("/ledger/payment-descriptions", { params: { q: form.description } }).then((r) => setSugs(r.data)).catch(() => setSugs([])), 200);
    return () => clearTimeout(t);
  }, [form.description, customer, showSugs]);

  const save = async () => {
    if (!(parseFloat(form.amount) > 0)) { toast.error("Tutar gerekli"); return; }
    setSaving(true);
    try {
      if (mode === "cheque") {
        await api.post("/cashbook/cheques", { direction: "in", customer_id: customer.id, amount: parseFloat(form.amount),
          bank: cheque.bank, cheque_no: cheque.cheque_no, due_date: cheque.due_date || null, note: form.description });
        toast.success(`${customer.short_name || customer.name}: çek portföye eklendi, cari güncellendi`);
      } else {
        await api.post("/ledger/payment", { ...form, customer_id: customer.id, amount: parseFloat(form.amount) });
        toast.success(`${customer.short_name || customer.name}: tahsilat kaydedildi`);
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const isCheque = mode === "cheque";

  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="customer-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Receipt className="h-5 w-5 text-primary" /> Tahsilat — {customer?.short_name || customer?.name}</DialogTitle>
        </DialogHeader>
        {customer && (
          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-sm flex justify-between" data-testid="customer-payment-balance">
              <span className="text-muted-foreground">Güncel bakiye</span>
              <span className={`font-mono font-semibold ${customer.balance > 0 ? "text-amber-400" : ""}`}>{TRY(customer.balance)}</span>
            </div>

            <div className="inline-flex rounded-xl bg-muted/50 p-1 w-full" data-testid="payment-mode-tabs">
              <button type="button" onClick={() => setMode("cash")} data-testid="payment-mode-cash" className={`flex-1 h-9 rounded-lg text-sm font-semibold transition-colors ${!isCheque ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Nakit / Havale</button>
              <button type="button" onClick={() => setMode("cheque")} data-testid="payment-mode-cheque" className={`flex-1 h-9 rounded-lg text-sm font-semibold transition-colors inline-flex items-center justify-center gap-1.5 ${isCheque ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}><ScrollText className="h-3.5 w-3.5" /> Çek ile</button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <F label="Tutar (₺)">
                <Input type="number" autoFocus value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && !isCheque && save()} data-testid="customer-payment-amount" className="font-mono" placeholder="0" />
              </F>
              <F label={isCheque ? "Vade Tarihi" : "Tarih"}>
                {isCheque
                  ? <Input type="date" value={cheque.due_date} onChange={(e) => setCheque({ ...cheque, due_date: e.target.value })} data-testid="customer-payment-cheque-due" className="font-mono" />
                  : <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="customer-payment-date" className="font-mono" />}
              </F>
            </div>

            {customer.balance > 0 && (
              <button type="button" onClick={() => setForm({ ...form, amount: String(Math.round(customer.balance * 100) / 100) })} data-testid="customer-payment-full"
                className="text-xs text-primary font-semibold hover:underline">Bakiyenin tamamını tahsil et ({TRY(customer.balance)})</button>
            )}

            {isCheque ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <F label="Banka"><Input value={cheque.bank} onChange={(e) => setCheque({ ...cheque, bank: e.target.value })} data-testid="customer-payment-cheque-bank" placeholder="Örn: Ziraat" /></F>
                  <F label="Çek No"><Input value={cheque.cheque_no} onChange={(e) => setCheque({ ...cheque, cheque_no: e.target.value })} data-testid="customer-payment-cheque-no" placeholder="0012345" className="font-mono" /></F>
                </div>
                <F label="Not"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="customer-payment-description" placeholder="Opsiyonel" /></F>
                <p className="text-xs text-teal-400">Çek portföye eklenecek, cari bakiyeden düşülecek. Nakit, çek tahsil edildiğinde kasaya girer.</p>
              </>
            ) : (
              <>
                <F label="Hesap (Kasa / Banka)">
                  <AccountSelect value={form.account_id} onChange={(id, a) => setForm({ ...form, account_id: id, method: a?.name || form.method })} testId="customer-payment-method" />
                </F>
                <div className="relative">
                  <F label="Açıklama">
                    <Input value={form.description} onChange={(e) => { setForm({ ...form, description: e.target.value }); setShowSugs(true); }}
                      onFocus={() => setShowSugs(true)} onBlur={() => setTimeout(() => setShowSugs(false), 150)} autoComplete="off"
                      data-testid="customer-payment-description" placeholder="Opsiyonel — örn: Nakit tahsilat, EFT…" />
                  </F>
                  {showSugs && sugs.length > 0 && (
                    <ul className="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-xl max-h-56 overflow-y-auto" data-testid="payment-description-suggestions">
                      {sugs.map((s, i) => (
                        <li key={i}>
                          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setForm((x) => ({ ...x, description: s.description })); setShowSugs(false); }} data-testid={`payment-suggestion-${i}`}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center justify-between gap-2">
                            <span className="truncate">{s.description}</span>
                            <span className="text-[10px] text-muted-foreground shrink-0">{s.count > 1 ? `${s.count}×` : ""}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="customer-payment-save" className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isCheque ? "Çeki Kaydet" : "Tahsilatı Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
